import React from 'react';
import { isEmpty } from 'lodash';
import { useRecoilValue } from 'recoil';

import {
  blockBenchmarkConfig,
  blockCustomizableMetricsMap,
  blockDoesNotSupportBenchmark,
  blockInfoGraphicType,
  blockLimitedRequestSubjects,
  blockSettings,
  customizedBlock,
  getBenchmarkSubject,
} from 'venn-state';
import {
  blockShowsInvestments,
  getRequestSubjectFromAnalysisSubject,
  hasDisabledBenchmark,
  hasDisabledIndividualBenchmark,
  hasDisabledRelativeToBenchmark,
} from 'venn-utils';

import { Block } from '../../core';
import { CommonBenchmark } from '../common-benchmark';
import { IndividualBenchmark } from './individual-benchmark';
import { InvestmentSubject } from './investment-subject';

import { useAppPrintMode } from '../../../../print';
import { getUniqueKeyFromStudioRequestSubject } from '../../../../studio-blocks';
import { shouldHideBenchmark } from '../../../utils';
import { PeerGroup } from '../peer-group';

interface DefaultSubjectsProps {
  id: string;
  metric?: string;
  relativeToBenchmark: boolean;
  forceShowSubjects?: boolean;
  hideSubjectsRow?: boolean;
  showPeerGroup?: boolean;
  isColorless?: boolean;
}

export const DefaultSubjects = ({
  id,
  metric,
  relativeToBenchmark,
  forceShowSubjects,
  hideSubjectsRow = false,
  showPeerGroup = false,
  isColorless = false,
}: DefaultSubjectsProps) => {
  const { inPrintModeOrReportLab } = useAppPrintMode();

  const benchmarkConfig = useRecoilValue(blockBenchmarkConfig(id));
  const { customBlockType } = useRecoilValue(blockSettings(id));
  const infoGraphicType = useRecoilValue(blockInfoGraphicType(id));

  const subjects = useRecoilValue(blockLimitedRequestSubjects(id));
  const hasIndividualBenchmarks =
    benchmarkConfig.type === 'INDIVIDUAL' && !hasDisabledIndividualBenchmark(customBlockType);

  const metricDefinitionsMap = useRecoilValue(blockCustomizableMetricsMap(id));
  const { selectedMetrics } = useRecoilValue(customizedBlock(id));

  const isBlockRelativeToBenchmark =
    relativeToBenchmark &&
    !hasDisabledBenchmark(customBlockType, infoGraphicType) &&
    !hasDisabledRelativeToBenchmark(customBlockType);

  const hideBenchmark =
    useRecoilValue(blockDoesNotSupportBenchmark(id)) ||
    shouldHideBenchmark(customBlockType, metricDefinitionsMap, selectedMetrics);

  const showInvestments = blockShowsInvestments(customBlockType);

  const filteredSubjects = subjects.filter(
    (subject) =>
      subject.subjectType !== 'INVESTMENT' ||
      (subject.subjectType === 'INVESTMENT' && (showInvestments === undefined || showInvestments)),
  );

  const requiresBenchmark = isBlockRelativeToBenchmark || (metric && metricDefinitionsMap[metric]?.needsBenchmark);
  const showSubjects =
    forceShowSubjects ||
    ((!inPrintModeOrReportLab || (inPrintModeOrReportLab && requiresBenchmark)) && !isEmpty(subjects));

  if (!showSubjects || (hideBenchmark && isEmpty(filteredSubjects))) {
    return null;
  }

  return (
    <Block.Subjects id={id}>
      {!hideBenchmark && benchmarkConfig?.subject && (
        <CommonBenchmark id={id} benchmarkSubject={benchmarkConfig?.subject} />
      )}
      {!hideBenchmark && benchmarkConfig?.subject && subjects.length > 0 && <Block.SubjectsSeparator />}

      {!hideSubjectsRow &&
        filteredSubjects.map((subject) => (
          <Block.SubjectsRow key={getUniqueKeyFromStudioRequestSubject(subject)}>
            <InvestmentSubject id={id} subject={subject} isColorless={isColorless} />
            {hasIndividualBenchmarks && !hideBenchmark && subject.individualBenchmark && (
              <>
                <Block.SubjectsSeparator />
                <IndividualBenchmark
                  id={id}
                  benchmark={getRequestSubjectFromAnalysisSubject(
                    getBenchmarkSubject(subject.individualBenchmark, subject.proxySetId),
                  )}
                />
              </>
            )}
          </Block.SubjectsRow>
        ))}

      {showPeerGroup && <PeerGroup id={id} />}
    </Block.Subjects>
  );
};
